import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import styled from 'styled-components';
import { ArrowLeft, Save } from 'lucide-react';
import { AdminLayout } from '../components/AdminLayout';
import { Button } from '../../components';
import { experiencesService } from '../../services';

const FormCard = styled.div`
  background: ${props => props.theme.colors.background.secondary};
  border: 1px solid ${props => props.theme.colors.border?.light || 'rgba(0,0,0,0.1)'};
  border-radius: ${props => props.theme.borderRadius.md};
  padding: 2rem;
  max-width: 720px;
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;

  label {
    display: block;
    font-weight: 600;
    color: ${props => props.theme.colors.text.primary};
    margin-bottom: 0.5rem;
    font-size: 0.9rem;
  }

  input, textarea {
    width: 100%;
    padding: 0.75rem 1rem;
    border: 1px solid ${props => props.theme.colors.border?.medium || 'rgba(0,0,0,0.2)'};
    border-radius: ${props => props.theme.borderRadius.base};
    background: ${props => props.theme.colors.background.primary};
    color: ${props => props.theme.colors.text.primary};
    font-family: inherit;
    font-size: 0.95rem;
    line-height: 1.5;
    transition: border-color 0.2s ease;

    &:focus {
      outline: none;
      border-color: ${props => props.theme.colors.primary};
    }
  }

  textarea {
    resize: vertical;
    min-height: 160px;
  }

  .hint {
    margin-top: 0.4rem;
    font-size: 12px;
    color: ${props => props.theme.colors.text.tertiary};
  }
`;

const FormActions = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 2rem;
`;

const BackLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  color: ${props => props.theme.colors.text.secondary};
  text-decoration: none;
  font-size: 0.9rem;
  margin-bottom: 1.5rem;

  &:hover {
    color: ${props => props.theme.colors.text.primary};
  }
`;

const EMPTY = { role: '', company: '', period: '', subtitle: '', description: '' };

export const ExperienceForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditing = Boolean(id);

  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(isEditing);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isEditing) return;
    experiencesService.getExperienceById(id)
      .then(data => setForm({
        role: data.role || '',
        company: data.company || '',
        period: data.period || '',
        subtitle: data.subtitle || '',
        description: data.description || ''
      }))
      .catch(() => navigate('/admin/experiences'))
      .finally(() => setLoading(false));
  }, [id, isEditing, navigate]);

  const handleChange = (e) => {
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.role.trim() || !form.company.trim()) {
      alert('Role and Company are required.');
      return;
    }
    setSaving(true);
    try {
      if (isEditing) {
        await experiencesService.updateExperience(id, form);
      } else {
        await experiencesService.createExperience(form);
      }
      navigate('/admin/experiences');
    } catch (error) {
      console.error('Error saving experience:', error);
      alert('Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout title={isEditing ? 'Edit Experience' : 'New Experience'}>
        <p style={{ color: 'var(--color-text-secondary)' }}>Loading...</p>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title={isEditing ? 'Edit Experience' : 'New Experience'}>
      <BackLink to="/admin/experiences">
        <ArrowLeft size={16} />
        Back to Experiences
      </BackLink>

      <FormCard>
        <form onSubmit={handleSubmit}>
          <FormGroup>
            <label>Role / Title *</label>
            <input
              name="role"
              value={form.role}
              onChange={handleChange}
              placeholder="e.g. Product Design Manager"
              required
            />
          </FormGroup>

          <FormGroup>
            <label>Company *</label>
            <input
              name="company"
              value={form.company}
              onChange={handleChange}
              placeholder="e.g. Flip Mobile App"
              required
            />
          </FormGroup>

          <FormGroup>
            <label>Period</label>
            <input
              name="period"
              value={form.period}
              onChange={handleChange}
              placeholder="e.g. 2023-Present"
            />
          </FormGroup>

          <FormGroup>
            <label>Subtitle / Focus Area</label>
            <input
              name="subtitle"
              value={form.subtitle}
              onChange={handleChange}
              placeholder="e.g. Domestic Transfer, Onboarding, and Home"
            />
            <p className="hint">Optional — shown below company name</p>
          </FormGroup>

          <FormGroup>
            <label>Description</label>
            <textarea
              name="description"
              value={form.description}
              onChange={handleChange}
              placeholder="Describe your role, impact, and key achievements..."
            />
            <p className="hint">Use double line breaks to separate paragraphs</p>
          </FormGroup>

          <FormActions>
            <Button type="submit" disabled={saving}>
              <Save size={16} />
              {saving ? 'Saving...' : isEditing ? 'Save Changes' : 'Create Experience'}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate('/admin/experiences')}
            >
              Cancel
            </Button>
          </FormActions>
        </form>
      </FormCard>
    </AdminLayout>
  );
};
